/**
 * ProblemIdLink — 문제 번호(PID)를 그리는 정본 부품.
 *
 * PID 는 두 메뉴 열한 자리에 나온다(문제 v2: 목록 표·목록 카드·상세 머리·분석 작업 목록·계보 /
 * 시험지 v2: 문항 편집·구성 창의 검색과 담은 목록·담기 창·채점·미리보기·문제 카드).
 * 2026-09-01 이전에는 그 열한 곳이 제각기 그렸다 — 10px 회색, 12px 회색, `PID: ` 접두, `#` 접두.
 * 같은 값이 자리마다 다른 얼굴이면 읽는 사람이 그때마다 「이게 그 번호가 맞나」를 한 번 더 생각한다.
 *
 * ── 정본 얼굴 (기준: 문제 v2 목록 표의 PID 칸 — 오너 지시 2026-09-01) ────────────
 *   고정폭(font-mono) · 굵게(font-bold) · 12px(text-xs) · **맨숫자**(접두어를 붙이지 않는다)
 *   색이 곧 상태다 — problemApi.problemIdTone: 완비면 브랜드 초록, 아직 빌 것이 있으면 먹색.
 *
 * 접두어를 걷은 이유(오너 결정 2026-09-01): 고정폭·굵은 글자 자체가 「이건 식별자다」를 말한다.
 * `#` 은 이 저장소에서 시험지 번호(#12)·배치 번호(#7)도 함께 쓰던 기호라 무슨 번호인지 갈리지
 * 않았고, `PID: ` 는 시험지 구성 창의 36px 짜리 세로 칸을 80px 로 벌려 그 행의 폭 배분을 깼다.
 *
 * ── 색을 모를 때 ────────────────────────────────────────────────────────────
 * need_* 네 플래그는 **검색 응답에만** 실린다(계약 §1-5). 시험지의 담은 문항 목록·채점 스냅샷처럼
 * 그 플래그가 없는 자리에서는 `item` 을 주지 않는다 → 먹색으로 서고 툴팁이 붙지 않는다.
 * ⚠ 이 부품이 null 을 직접 가려내는 이유: getNeedBadges(null) 이 빈 배열이라
 *   problemIdTone(null) 은 그것을 **완비로 읽어 초록을 칠한다.** 모르는 것을 아는 척하게 된다.
 *
 * ── 링크 ────────────────────────────────────────────────────────────────────
 * 번호는 그 문제의 상세로 가는 길이다(/problem-hub/problem/:id — 오너 지시 2026-09-01).
 * button 이 아니라 진짜 <a> 로 그린다: Ctrl·⌘ 클릭으로 새 탭이 열리는 것은 브라우저가 하는
 * 일이고, button 으로는 그 길이 아예 없다. 그래서 눌림을 가로챌 때도 **꾸밈 클릭은 그냥 보낸다.**
 *
 * `linked={false}` 는 두 경우뿐이다:
 *   ① 바깥(행·카드 전체)이 이미 **같은 곳으로 가는 길**일 때 — 길을 두 개 겹쳐 두지 않는다.
 *   ② 그 자리가 button 안이라 anchor 를 넣으면 버튼 안의 버튼이 될 때(HTML 위반).
 *
 * ── 미저장 편집 가드 ────────────────────────────────────────────────────────
 * 시험지 구성 창처럼 저장하지 않은 편집을 든 화면에서 번호를 누르면 그 편집이 조용히 사라진다.
 * 화면이 <ProblemNavGuard confirm={…}> 로 자기 확인 함수를 담아 두면 이 부품이 이동 전에 부르고,
 * false 를 받으면 이동하지 않는다. 겹쳐 두면 **안쪽이 이긴다** — 창이 페이지를 덮고 있으므로
 * 지금 잃을 것을 아는 쪽은 창이다.
 */
import React, { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { problemIdTone } from './problemApi';

/** 문제 상세로 가는 한 길. 라우트는 App.js 의 `/problem-hub/problem/:problemId` 다 */
export const problemDetailPath = (id) => `/problem-hub/problem/${id}`;

const NavGuardContext = createContext(null);

/** confirm: () => boolean — 이동해도 되는지 묻는다. **참조가 고정돼야 한다**(useCallback) */
export function ProblemNavGuard({ confirm, children }) {
  return <NavGuardContext.Provider value={confirm}>{children}</NavGuardContext.Provider>;
}

/**
 * 같은 가드를 **문제 번호 말고 다른 링크**도 지나가게 한다(2026-09-20 — 시험지 배포 목록의 학생명).
 * 화면이 깔아 둔 확인 함수는 「이 화면을 떠난다」에 걸리는 것이지 문제 번호에만 걸리는 것이 아니다.
 * 가드가 없는 화면에서는 null 이 돌아온다 — 그때는 그냥 이동한다.
 */
export function useNavGuard() {
  return useContext(NavGuardContext);
}

/**
 * 크기는 두 단뿐이다. `md`(12px)가 기준이고, `lg`(14px)는 **상세 머리 한 자리**에만 쓴다 —
 * 거기서는 번호가 그 화면의 제목이라 탭 글자와 같은 크기로 서면 제목으로 읽히지 않는다.
 * 위계를 만드는 것은 크기이고, 같은 것으로 묶는 것은 고정폭·굵기·색이다.
 */
const SIZE = { md: 'text-xs', lg: 'text-sm' };

/**
 * 꾸밈 클릭(새 탭·새 창·저장)은 브라우저 몫이다 — 가로채면 그 길이 막힌다.
 * 밖으로 내보낸 이유: 같은 판정을 링크마다 따로 적으면 한쪽만 고쳐졌을 때 어떤 링크는 ⌘ 클릭으로
 * 새 탭이 열리고 어떤 링크는 안 열린다. 사용자에게는 그것이 「가끔 안 되는 기능」으로 보인다.
 */
export const isModifiedClick = (e) => e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;

function ProblemIdLink({
  id,
  /** 검색 응답 한 건 — need_* 가 실려 있을 때만 준다. 없으면 색은 먹색으로 굳는다 */
  item = null,
  linked = true,
  /**
   * chip — 계보 칩(「원본 61577」·「등록 61590」)이나 문장 속에 번호가 박히는 자리.
   * 칩은 제 배경색·글자색·글자 크기를 이미 갖고 있다. 거기에 색과 크기를 덧칠하면 알약 하나가
   * 두 목소리로 말하고(10px 칩 안에 12px 숫자), 칩의 키가 숫자 때문에 들쭉날쭉해진다.
   * 그래서 이 모드는 **고정폭과 굵기만** 가져간다 — 그 둘이 「이건 식별자다」를 말하는 몫이다.
   */
  chip = false,
  /** 주면 **기본 이동을 대신한다**(모달을 닫고 탭을 정한 뒤 옮기는 자리 등) */
  onOpen = null,
  size = 'md',
  className = '',
  title = null,
}) {
  const guard = useContext(NavGuardContext);
  if (id == null) return null;

  const t = !chip && item ? problemIdTone(item) : null;
  // problemIdTone 의 className 이 굵기를 이미 갖는다 — 여기서 또 주면 같은 말이 두 번이다
  const face = chip
    ? 'font-mono font-bold'
    : `font-mono ${SIZE[size] || SIZE.md} ${t ? t.className : 'font-bold text-gray-900'}`;
  const tip = title ?? t?.title;

  if (!linked) {
    return <span title={tip || undefined} className={`${face} ${className}`}>{id}</span>;
  }

  return (
    <Link
      to={problemDetailPath(id)}
      title={tip || undefined}
      aria-label={`${id}번 문제 열기`}
      onClick={(e) => {
        // 바깥 행·카드는 대개 **다른 일**을 한다(고르기·펴기·지면으로 가기) — 한 번 눌러 둘이 돌지 않게
        e.stopPropagation();
        if (isModifiedClick(e)) return;
        if (guard && !guard()) { e.preventDefault(); return; }
        if (onOpen) { e.preventDefault(); onOpen(id); }
      }}
      className={`${face} underline-offset-2 hover:underline ${className}`}
    >
      {id}
    </Link>
  );
}

export default ProblemIdLink;
